// Imports ------------------------------------------------------------------->

import {Service, service, inject} from "./di";
import {AuthProvider} from "../definitions/CommonDefinitions";
import {ConfigService} from "./ConfigService";
import { AuthStatus, AuthUser, IAuthenticationService } from "./definitions/IAuthenticationService";
import {DefaultAuthenticator} from "./auth/DefaultAuthenticator";
import { LocationService } from "./LocationService";
import { EmailAuthenticator } from "./auth/EmailAuthenticator";
import { generateUUID } from "../utils/uuid";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->
export interface UserData {
	email: string;
	password: string;
}

export interface ConfirmChangePasswordData {
	email: string;
	password: string;
	confirmation_code: string;
}

export interface ChangePasswordData {
	previous_password: string;
	new_password: string;
}

export interface ErrorData {
	code: string;
	message: string;
	title: string;
}

export interface User {
	created_at: string;
	updated_at: string;
	id: string;
	api_access_token: string;
	active: boolean;
	subscribed: any;
	email_subscribed: any;
	shopping_lists: any[];
	anonymous: boolean;
	devices: any[];
	settings: any;
	revision: number;
	privacy: any;
	email?: string;
}

export interface ConfirmEmailData {
	email: string;
	confirmation_code: string;
}

export interface UserResponse {
	user: User;
	email: string;
	third_party_data: any;
	validated: boolean;
}

@service("authenticationService")
export class AuthenticationService extends Service implements IAuthenticationService {

	@inject config: ConfigService;
	@inject locationService: LocationService;

	private delegate: IAuthenticationService;
	private emailAuthenticator: EmailAuthenticator;

	init(state?: any) {
		if (this.delegate) {
			return Promise.resolve();
		}
		this.delegate = new DefaultAuthenticator();
		this.emailAuthenticator = new EmailAuthenticator();
		return this.delegate.init(state);
	}
	
	get status() {
		const infoCookie = Cookies.getJSON("flipp-user");
		return infoCookie ? AuthStatus.LOGGED_IN : AuthStatus.LOGGED_OUT;
	}
	
	get user() {
		const infoCookie = Cookies.getJSON("flipp-user");
		const tokenCookie = Cookies.getJSON("flipp-login");
		if (infoCookie && tokenCookie)
			return {
				info: infoCookie,
				token: tokenCookie.token,
			} as AuthUser;
		else return null;
	}

	register(data: UserData) {
		return this.delegate.register(data);
	}

	finishRegistration(data: ConfirmEmailData) {
		return this.delegate.finishRegistration(data);
	}

	login(source: AuthProvider, target?: EventTarget, data?: any) {
		return this.delegate.login(source, target, data);
	}

	logout(target?: EventTarget) {
		return this.delegate.logout(target);
	}

	sendPasswordResetCode(email: string) {
		return this.emailAuthenticator.makeRequest("send_password_reset_code", {
			email,
			account_guid: window.btoa(generateUUID())
		});
	}

	confirmChangePassword(body: ConfirmChangePasswordData) {
		return this.emailAuthenticator.makeRequest("confirm_change_password", body);
	}

	changePassword(body: ChangePasswordData) {
		const user = JSON.parse(Cookies.get("flipp-user"));
		return this.emailAuthenticator.makeRequest("change_password", {
			...body,
			account_guid: user.id
		}, user.accessToken);
	}

	resendConfirmationCode(email: string) {
		return this.emailAuthenticator.makeRequest("resend_confirm_code", {email});
	}

	getPasswordRequirements() {
		return this.emailAuthenticator.makeRequest(`password_requirements?locale=${this.locationService.lang}`, null, null, "GET");
	}
}
